/* eslint-disable no-continue, no-await-in-loop, no-restricted-syntax, class-methods-use-this */
import {
  createStore,
  get,
  set,
  del,
  keys,
} from 'idb-keyval';
import { v4 as uuidv4 } from 'uuid';

const DB_NAME = 'climate-champions-offline';
const STORE_NAME = 'upload-queue';

/**
 * A simple IndexedDB-backed queue for offline uploads.
 *
 * Uses a claim/ack flow:
 * 1. `enqueue()` adds an item with status 'queued'
 * 2. `claimNext()` marks the next eligible item as 'inflight' and returns it
 * 3. On success, call `ack(id)` to remove the item
 * 4. On failure, call `fail(id, error)` to mark it as 'failed' for later retry
 *
 * Items are stored as normalized payloads (plain objects + optional File/Blob).
 * The consumer is responsible for reconstructing FormData when uploading.
 */
class OfflineUploadQueue {
  constructor() {
    this._store = createStore(DB_NAME, STORE_NAME);
  }

  async _get(id) {
    return get(id, this._store);
  }

  async _set(id, item) {
    await set(id, item, this._store);
  }

  /**
   * Generates a sortable unique ID (creation order via lexicographic sort).
   * Format: "{13-digit-timestamp}-{uuid}"
   * @returns {string}
   * @private
   */
  _makeId() {
    const ts = String(Date.now()).padStart(13, '0');
    return `${ts}-${uuidv4()}`;
  }

  /**
   * Add an item to the queue.
   * 
   * @param {Object} options
   * @param {'story' | 'plan'} options.type - The type of upload
   * @param {Object} options.payload - Normalized payload object (may include File/Blob fields)
   * @returns {Promise<string>} The generated item ID
   * 
   * @example
   * const id = await queue.enqueue({
   *   type: 'story',
   *   payload: {
   *     translations: { en: { title: 'My Story', description: '...' } },
   *     lat: -13.5,
   *     lng: 33.5,
   *     address: 'Kasungu, Malawi',
   *     hazardCategories: ['drought', 'flood'],
   *     creatorName: 'John',
   *     imageFile: someFileObject, // optional File/Blob
   *   },
   * });
   */
  async enqueue({ type, payload }) {
    const id = this._makeId();
    const item = {
      id,
      type,
      payload,
      createdAt: Date.now(),
      status: 'queued',
      attempts: 0,
      lastAttemptAt: null,
      lastError: null,
      nextAttemptAt: null,
      dead: false,
    };
    await this._set(id, item);
    return id;
  }

  /**
   * Claim the next available item for processing.
   * Marks the item as 'inflight' and increments the attempt counter.
   *
   * @returns {Promise<Object|null>} The claimed item, or null if queue is empty
   *
   * @example
   * const item = await queue.claimNext();
   * if (item) {
   *   try {
   *     await uploadItem(item);
   *     await queue.ack(item.id);
   *   } catch (e) {
   *     await queue.fail(item.id, e.message);
   *   }
   * }
  */
  async claimNext() {
    const now = Date.now();
    return this._store('readwrite', store => new Promise((resolve, reject) => {
      const { transaction } = store;
      let candidate = null;
      let claimed = null;
      let hasInflight = false;

      transaction.oncomplete = () => resolve(claimed);
      transaction.onabort = () => reject(transaction.error);
      transaction.onerror = () => reject(transaction.error);

      const request = store.openCursor();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cursor = request.result;

        if (!cursor) {
          if (!candidate || hasInflight) {
            claimed = null;
            return;
          }

          claimed = {
            ...candidate,
            status: 'inflight',
            attempts: (candidate.attempts || 0) + 1,
            lastAttemptAt: now,
            lastError: null,
            nextAttemptAt: null,
          };

          store.put(claimed, candidate.id);
          return;
        }

        const item = cursor.value;

        if (item && !item.dead) {
          if (item.status === 'inflight') {
            hasInflight = true;
          }

          if (!candidate) {
            const claimable = item.status === 'queued' || item.status === 'failed';
            const isReady = !item.nextAttemptAt || item.nextAttemptAt <= now;

            if (claimable && isReady) {
              candidate = item;
            }
          }
        }

        cursor.continue();
      };
    }));
  }

  /**
   * Acknowledge successful processing of an item (removes it from the queue).
   * 
   * @param {string} id - The item ID
   * @returns {Promise<void>}
   */
  async ack(id) {
    await del(id, this._store);
  }

  /**
   * Mark an item as failed after an unsuccessful upload attempt.
   * The item remains in the queue for later retry.
   * 
   * @param {string} id - The item ID
   * @param {string|Error} error - The error message or Error object
   * @returns {Promise<void>}
   */
  async fail(id, error, options = {}) {
    const item = await this._get(id);

    if (!item) {
      return;
    }

    const {
      nextAttemptAt = null,
      dead = false,
    } = options;

    await this._set(id, {
      ...item,
      status: 'failed',
      lastError: String(error || 'Unknown error'),
      nextAttemptAt,
      dead,
    });
  }

  /**
   * Get the total number of items in the queue.
   * 
   * @returns {Promise<number>}
   */
  async count() {
    return (await keys(this._store)).length;
  }

  async requeueInflightOlderThan(staleMs) {
    const now = Date.now();
    const allKeys = await keys(this._store);
    allKeys.sort();

    let changed = 0;

    for (const id of allKeys) {
      const item = await this._get(id);
      if (!item || item.status !== 'inflight') continue;

      const lastAttemptAt = item.lastAttemptAt || 0;
      if (now - lastAttemptAt < staleMs) continue;

      await this._set(id, {
        ...item,
        status: 'failed',
        lastError: item.lastError || 'Recovered inflight item',
        nextAttemptAt: now,
      });
      changed += 1;
    }

    return changed;
  }

  /**
   * Remove a specific item from the queue.
   * 
   * @param {string} id - The item ID
   * @returns {Promise<void>}
   */
  async remove(id) {
    await del(id, this._store);
  }

  /**
   * Clear all items from the queue.
   * 
   * @returns {Promise<void>}
   */
  async clear() {
    const allKeys = await keys(this._store);
    for (const id of allKeys) {
      await del(id, this._store);
    }
  }
}

export default OfflineUploadQueue;
